import { useEffect, useMemo, useState } from 'react';
import { FormikErrors } from "formik";
import { Logger } from "@utils/logger";
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { AttributeStatusTypes } from '@enums/attribute-status-types';
import { EmailPINDeliveryPreference, SMSPINDeliveryPreference, VoicePINDeliveryPreference } from '@constants/form-constants';
import { AXNUIRadioGroupInput } from '@iddataweb/idw-common-ui/containers';
import styles from "./email-or-phone-pin-preference-input.less";
import { useSelector } from 'react-redux';
import { getLanguageContent } from '@redux/selectors/language-selectors';

type EmailOrPhonePINPreferenceInputProps = {
	attribute: AttributeFormState;
	fieldName: string;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	onSetSubmitValue: (payload: any) => void;
	isSubmitting: boolean;
};

export const EmailOrPhonePINPreferenceInput = (props: EmailOrPhonePINPreferenceInputProps) => {
	const {
		attribute,
		onSetSubmitValue,
		fieldName,
		setFieldValue,
		setFieldTouched,
		isSubmitting
	} = props;

	const [hasPerformedForcedUpdate, setHasPerformedForcedUpdate] = useState(false);

	const content = useSelector(getLanguageContent);
	const setFieldValueInternal = (newValue: string) => {
		setFieldValue(fieldName, newValue);
		const inputData = {
			emailorphonepinpreference: newValue
		};
		onSetSubmitValue({ inputData });
	};

	const emailOrPhonePinPreferences: Array<{ value: string; label: string; label2: string }> = useMemo(() => [
		{
			value: SMSPINDeliveryPreference,
			label: content.form.pinPreferenceInput.sms,
			label2: content.form.pinPreferenceInput.smsText
		},
		{
			value: VoicePINDeliveryPreference,
			label: content.form.pinPreferenceInput.voice,
			label2: content.form.pinPreferenceInput.voiceText
		},
		{
			value: EmailPINDeliveryPreference,
			label: content.form.pinPreferenceInput.email,
			label2: content.form.pinPreferenceInput.emailText
		}
	], [content]);

	const initializeEmailOrPhonePinDeliveryPreferenceInput = async (value: string) => {
		const inputData = { ...attribute.inputData, emailorphonepinpreference: value };
		Logger.info('[EmailOrPhonePINPreferenceInput] submit value set:', inputData);
		await setFieldValue(fieldName, value);
		onSetSubmitValue({ inputData });
		setFieldTouched(fieldName, true, true);
	};

	useEffect(() => {
		if (
			attribute.isPlaceholder &&
			attribute.prefillValueMap.emailorphonepinpreference &&
			!attribute.inputData.emailorphonepinpreference
		) {
			const selectedOption = emailOrPhonePinPreferences.find(afo => afo.value === attribute.prefillValueMap.emailorphonepinpreference);
			if (selectedOption) {
				initializeEmailOrPhonePinDeliveryPreferenceInput(selectedOption.value)
			}
		} else if (!attribute.inputData.emailorphonepinpreference && attribute.status === AttributeStatusTypes.NEW) {
			initializeEmailOrPhonePinDeliveryPreferenceInput(emailOrPhonePinPreferences[0].value);
		}
	}, [
		attribute.isPlaceholder,
		attribute.prefillValueMap.emailorphonepinpreference,
		attribute.inputData.emailorphonepinpreference,
		attribute.status
	]);

	useEffect(() => {
		if (!hasPerformedForcedUpdate && attribute.inputData.emailorphonepinpreference) {
			setHasPerformedForcedUpdate(true);
			setFieldValueInternal(attribute.inputData.emailorphonepinpreference);
		}
	}, [hasPerformedForcedUpdate, attribute.inputData.emailorphonepinpreference]);

	return (
		<div className={styles.emailOrPhonePinPreferenceInputContainer}>
			<AXNUIRadioGroupInput
				value={attribute.inputData.emailorphonepinpreference}
				idPrefix={`attr${attribute.id}`}
				preferences={emailOrPhonePinPreferences}
				onChange={setFieldValueInternal}
				isSubmitting={isSubmitting}
				isPlaceholder={attribute.isPlaceholder}
                isDisabled={attribute.isDisabled}
			/>
			{attribute.isPlaceholder && (
				<div>
					<span>{content.form.pinPreferenceInput.pinVerified}</span>
				</div>
			)}
		</div>
	);
};